import { Component, inject, NgZone, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { AppWebBridgeService } from "@services/app-web-bridge-service";
import { BroadcastService } from "@services/broadcast-service";
import { ConnectionService } from "@services/connection-service";
import { EventLoggerService } from "@services/event-logger-service";
import { WindowRefService } from "@services/window-ref-service";
import { Subscription } from "rxjs";
import { AppConfig } from "../../app.config";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { CityClimateInfoComponent } from "@components/city-climate-info/city-climate-info.component";
import { CurrentComponentService } from "@services/current-component";

@Component({
  selector: "onboarding-location",
  templateUrl: "./onboarding-location.component.html",
  standalone: false,
})
export class OnboardingLocationComponent implements OnDestroy {
  private readonly appWebBridge: AppWebBridgeService =
    inject(AppWebBridgeService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly router: Router = inject(Router);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly window: WindowRefService = inject(WindowRefService);
  private readonly zone: NgZone = inject(NgZone);
  protected readonly appConfig: AppConfig = inject(AppConfig);
  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private readonly currentComponent: CurrentComponentService = inject(CurrentComponentService);

  subscriptions: Subscription[] = [];
  user: any;
  showInfo: boolean = false;
  loading: boolean = false;

  ngOnInit(): void {
    this.user = this.conn.getActingUser();

    this.currentComponent.set(this);

    this.eventLogger.cleverTapEvent(
      "pageOpen",
      JSON.stringify({ pageName: "onboarding-location" })
    );
  }

  openCitySheet(): void {
    const bottomsheetRef = this.bottomSheet.open(CityClimateInfoComponent);
    bottomsheetRef.afterDismissed().subscribe((response: boolean) => {
      if (response === true) this.getLocation();
      else if (response === false) this.continue();
    });
  }

  async continue(): Promise<void> {
    await this.conn.updateUserData({ lastLocation: {} });
    if (this.showInfo) {
      this.eventLogger.cleverTapEvent(
        "click",
        JSON.stringify({ name: "Onboarding-Location-Details-Popup" })
      );
    }
    this.eventLogger.cleverTapEvent(
      "click",
      JSON.stringify({ name: "onboardingLocationAccessDenied" }),
      [
        this.appConfig.Shared.EventTracker.CLEVER_TAP,
        this.appConfig.Shared.EventTracker.MOENGAGE,
        this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
      ]
    );

    this.zone.run(async (): Promise<void> => {
      await this.router.navigate(["/onboarding/concern"], {
        queryParams: { redirectOnBack: "/home" },
      });
    });
  }

  async getLocation(): Promise<any> {
    this.eventLogger.cleverTapEvent(
      "click",
      JSON.stringify({ name: "onboardingLocationAccessGranted" })
    );
    if (this.showInfo) {
      this.eventLogger.cleverTapEvent(
        "click",
        JSON.stringify({ name: "Onboarding-Location-Details-Popup" })
      );
    }
    this.loading = true;
    // Native location
    if (this.appWebBridge.getLocationDetails()) {
      this.loading = false;
      await this.router.navigate(["/onboarding/concern"], {
        queryParams: { redirectOnBack: "/home" },
      });
      return;
    }
    // Browser location
    this.window.nativeWindow.navigator.geolocation.getCurrentPosition(
      async (location: any): Promise<void> => {
        this.loading = false;
        await this.setUserLocation({
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        });
        await this.router.navigate(["/onboarding/concern"], {
          queryParams: { redirectOnBack: "/home" },
        });
      },
      (): any => {
        this.loading = false;
        this.broadcast.broadcast("NOTIFY", {
          message:
            "Location permission denied. Kindly enable permission in your browser settings.",
        });
      }
    );
  }

  async setUserLocation(lastLocation: any): Promise<void> {
    await this.conn.updateUserData({ lastLocation: lastLocation || {} });
  }

  async handleBackPress(): Promise<boolean> {
    return true;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: any): void => sub.unsubscribe());
    this.currentComponent.remove(this);
  }
}
